import { ApiError, describeApiError } from '@brush/api-client';
import { useActingRole, type ActingRole } from '@brush/auth';
import { ErrorRetry, RoleChooser, Spinner, type RoleChoice } from '@brush/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CardsScreen } from './components/CardsScreen';
import { CenteredScreen } from './components/CenteredScreen';
import { FantasyView } from './components/fantasy/FantasyView';
import { HomeView } from './components/HomeView';
import { LeaderboardsView } from './components/LeaderboardsView';
import { PlayerPicker } from './components/PlayerPicker';
import { PortalShell } from './components/PortalShell';
import { RatingsView } from './components/RatingsView';
import { StatsView } from './components/StatsView';
import type { PortalView } from './nav';
import { usePlayer } from './player';
import { portalFailureMessage } from './portalFailure';
import { useCurrentUser } from './queries';
import { roleChoicesFor, STALE_ROLE_NOTICE } from './roles';
import { useSession, useSessionStarted } from './api';

export function App() {
  const { player, canBrowse, isLoading, isError, error, refetch, theme } = usePlayer();
  const [view, setView] = useState<PortalView>('home');
  const [showCards, setShowCards] = useState(false);
  const me = useCurrentUser();
  const { signOut, displayName } = useSession();
  useSessionStarted(me.data !== undefined);
  const { actingRole, choose, clear } = useActingRole();
  const queryClient = useQueryClient();
  const [choiceWasRefused, setChoiceWasRefused] = useState(false);

  // A stored choice the server has stopped accepting — an assignment withdrawn between sessions.
  // Clearing it changes the /api/me key, so the roles this account may still act as come back
  // from the server rather than from a guess here; the flag survives that round trip so the
  // chooser can say why it is asking again.
  const refusedChoice = me.error instanceof ApiError && me.error.code === 'acting_role_not_held';

  useEffect(() => {
    if (!refusedChoice) return;
    setChoiceWasRefused(true);
    clear();
  }, [refusedChoice, clear]);

  const choices = roleChoicesFor(me.data?.availableRoles ?? []);

  const pickRole = (id: string) => {
    setChoiceWasRefused(false);
    choose(id as ActingRole);
    // Everything cached was read as somebody else — and in this app that includes whose portal
    // is on screen. Clearing is the honest reset.
    queryClient.clear();
  };

  // Wrapped in `.onair`, which is what inverts the shared tokens to the portal's charcoal
  // ground. PortalShell carries that class normally, and this renders instead of it.
  const chooser = (notice: string | null, options: RoleChoice[]) => (
    <div className="onair">
      <RoleChooser
        appName="the Brush portal"
        choices={options}
        onChoose={pickRole}
        notice={notice}
        currentId={actingRole}
        accountName={displayName}
        onSignOut={signOut}
      />
    </div>
  );

  // The effect above is clearing the refused choice; what follows is the refetch it triggers.
  if (refusedChoice) {
    return (
      <CenteredScreen background={theme.bg}>
        <Spinner label="Loading portal…" />
      </CenteredScreen>
    );
  }

  if (isLoading) {
    return (
      <CenteredScreen background={theme.bg}>
        <Spinner label="Loading portal…" />
      </CenteredScreen>
    );
  }
  if (isError) {
    return (
      <CenteredScreen background={theme.bg}>
        {/* What the answer said, or that there was none (#230) — never "couldn't reach" for an answered 401 or 404. */}
        <ErrorRetry message={portalFailureMessage(error, describeApiError)} onRetry={refetch} />
      </CenteredScreen>
    );
  }
  // Only when there is a genuine choice to make. One role is not a decision.
  if (choices.length > 1 && actingRole === null) {
    return chooser(choiceWasRefused ? STALE_ROLE_NOTICE : null, choices);
  }

  // Staff choose whose portal to view; the picker is the landing screen until they do.
  if (!player && canBrowse) return <PlayerPicker />;

  // Two of ADR 0010 Decision 6's three states, and `CardsScreen` is both: no card connected — the
  // discovery view, and a first-class screen rather than the dead end it replaces — or more than
  // one connected with no preference stored yet, which is a choice and gets asked. The third state
  // is the one that never lands here: a single card resolves in `player.tsx` and goes straight in.
  if (!player) return <CardsScreen />;

  // Opened from the chrome, once there is a portal to come back to.
  if (showCards) return <CardsScreen onClose={() => setShowCards(false)} />;

  return (
    <PortalShell view={view} onNavigate={setView} onOpenCards={() => setShowCards(true)}>
      {view === 'home' ? (
        <HomeView onNavigate={setView} />
      ) : view === 'leaderboards' ? (
        <LeaderboardsView />
      ) : view === 'ratings' ? (
        <RatingsView />
      ) : view === 'fantasy' ? (
        <FantasyView />
      ) : (
        <StatsView />
      )}
    </PortalShell>
  );
}
